/*
 * Copyright (c)  CYBERKOALA LLC 2025.
 * Proprietary License.
 * Redistribution and use in source and binary forms, with or without
 * modification, are restricted.
 */

import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, } from '@heroui/react';
import { Button } from '@/components/ui/button';
import React from 'react';
import type { ModalProps } from '@/stores/use-modal-store.ts';
import { useDmCallStore } from '@/stores/use-dm-call-store.ts';
import { useNavigate } from 'react-router';
import { PhoneIcon, PhoneOffIcon } from 'lucide-react';
import { useGlobalSFX } from '@/context/sfx-context/use-sfx.ts';
import { useSettings } from '@/context/settings-context/settings-context.ts';
import { UserAvatar } from '@/components/user-avatar.tsx';
import { useTranslation } from 'react-i18next';

/**
 * @param isOpen
 * @param onClose
 * @param data
 * @constructor
 */
export const IncomingCallModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const navigate = useNavigate();
  const { openCall } = useDmCallStore();
  const sfx = useGlobalSFX();
  const { notifications } = useSettings();
  const { t } = useTranslation();

  if (!data) return null;

  sfx.incomingCall.play();

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      backdrop="blur"
    >
      <ModalContent>
        <ModalHeader>
          {t('incoming_call_modal.title')}
        </ModalHeader>

        <ModalBody>
          <div className="flex items-center gap-3">
            <UserAvatar
              src={data.avatarUrl}
              name={data.userName}
              className="size-12 rounded-full object-cover"
            />
            <div className="flex flex-col">
              <span className="font-semibold">{data.userName}</span>
              <span className="text-sm text-muted-foreground">
                {t('incoming_call_modal.calling_you')}
              </span>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="destructive"
            onClick={onClose}
          >
            <PhoneOffIcon className="text-red-500" />
            {t('incoming_call_modal.decline')}
          </Button>

          <Button
            variant="primary"
            onClick={() => {
              navigate(`/channels/@me/${data.userId}`);
              if (data.conversationId)
              {
                openCall(data.conversationId, '');
              }

              onClose();
            }}
          >
            <PhoneIcon/>
            {t('incoming_call_modal.accept')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
